import type {ZodType} from "zod";
import {z as zod} from "zod";
import {AssetsPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/assets/domain/AssetsPlatformAccessOperation";
import {AuthenticationPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/authentication/domain/AuthenticationPlatformAccessOperation";
import {AuthorizationPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/authorization/domain/AuthorizationPlatformAccessOperation";
import {GeographyPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/geography/domain/GeographyPlatformAccessOperation";
import {NotificationsPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/notifications/domain/NotificationsPlatformAccessOperation";
import {OpenApiPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/openApi/domain/OpenApiPlatformAccessOperation";
import {OperationForAuthorization} from "@vesta/vos-types/modules/platformLevel/authorization/domain/OperationForAuthorization";
import {StorageDatabasesPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/storageDatabases/domain/StorageDatabasesPlatformAccessOperation";
import {TimeZonesPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/timeZones/domain/TimeZonesPlatformAccessOperation";
import {UserDetailsPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetailsPlatformAccessOperation";
import {WorkspacesPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/WorkspacesPlatformAccessOperation";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export enum PlatformModuleForAuthorization {
    assets = "2c568072-00a5-43a3-ae6b-eaccda335ce9",
    authentication = "d5f1dc33-b234-4990-98bd-d4834910d646",
    authorization = "036f6125-20fe-4c06-b348-3ce1e113f140",
    // countries = "bfbac22b-14d0-4247-868a-445dc889c834",
    geography = "ee580d05-24e0-42d5-aea3-f531d76156cf",
    notifications = "3285107c-c10c-41bd-a0e2-8f0614f21215",
    openApi = "d9e76437-7fd3-4081-97f2-b5dcebf98cba",
    storageDb = "2405eab9-7ec5-41d2-8f91-e1d1ee3a87db",
    userProfiles = "70903925-18de-4d97-8aef-ea0712854c7b",
    workspace = "53d16bda-2891-4c71-a394-f63a7b7514fa",
    timeZones = "dd1db84a-b877-401f-83b0-ca2ed21b563f",
};

export type PlatformAccessOperationTypeMap = {
    [PlatformModuleForAuthorization.assets]: AssetsPlatformAccessOperation;
    [PlatformModuleForAuthorization.authentication]: AuthenticationPlatformAccessOperation;
    [PlatformModuleForAuthorization.authorization]: AuthorizationPlatformAccessOperation;
    [PlatformModuleForAuthorization.notifications]: NotificationsPlatformAccessOperation;
    [PlatformModuleForAuthorization.openApi]: OpenApiPlatformAccessOperation;
    [PlatformModuleForAuthorization.storageDb]: StorageDatabasesPlatformAccessOperation;
    [PlatformModuleForAuthorization.userProfiles]: UserDetailsPlatformAccessOperation;
    [PlatformModuleForAuthorization.workspace]: WorkspacesPlatformAccessOperation;
    [PlatformModuleForAuthorization.geography]: GeographyPlatformAccessOperation;
    [PlatformModuleForAuthorization.timeZones]: TimeZonesPlatformAccessOperation;
};

export type PlatformAccessOperation = PlatformAccessOperationTypeMap[PlatformModuleForAuthorization];

export const platformAccessOperationMap: {
    readonly [T in PlatformModuleForAuthorization]: readonly PlatformAccessOperationTypeMap[T][];
} = {
    [PlatformModuleForAuthorization.assets]: Object.values(AssetsPlatformAccessOperation),
    [PlatformModuleForAuthorization.authentication]: Object.values(AuthenticationPlatformAccessOperation),
    [PlatformModuleForAuthorization.authorization]: Object.values(AuthorizationPlatformAccessOperation),
    [PlatformModuleForAuthorization.notifications]: Object.values(NotificationsPlatformAccessOperation),
    [PlatformModuleForAuthorization.openApi]: Object.values(OpenApiPlatformAccessOperation),
    [PlatformModuleForAuthorization.storageDb]: Object.values(StorageDatabasesPlatformAccessOperation),
    [PlatformModuleForAuthorization.userProfiles]: Object.values(UserDetailsPlatformAccessOperation),
    [PlatformModuleForAuthorization.workspace]: Object.values(WorkspacesPlatformAccessOperation),
    [PlatformModuleForAuthorization.geography]: Object.values(GeographyPlatformAccessOperation),
    [PlatformModuleForAuthorization.timeZones]: Object.values(TimeZonesPlatformAccessOperation),
};

export type GetPlatformAccessOperationType<T extends PlatformModuleForAuthorization> = T extends PlatformModuleForAuthorization
    ? PlatformAccessOperationTypeMap[T]
    : never;

export type PlatformPermissionMatrix = Partial<Record<PlatformModuleForAuthorization, Record<OperationForAuthorization, boolean>>>;

export const platformPermissionMatrixValidator: ZodType<PlatformPermissionMatrix> = zod.partialRecord(
    zod.enum(PlatformModuleForAuthorization),
    zod.record(zod.enum(OperationForAuthorization), zod.boolean()),
);

export type PlatformAuthorization = {
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    id: Uuid;
    permissions: PlatformPermissionMatrix;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    userId: Uuid;
};

export const platformAuthorizationValidator: ZodType<PlatformAuthorization> = zod.object({
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    id: uuidZodValidator,
    permissions: platformPermissionMatrixValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    userId: uuidZodValidator,
});
